// @flow
import { useState, useEffect } from "react";

import { useModularUIRequest } from "./useModularUIRequest";

import { HTTP_METHODS } from "../constants";

import ListModel from "../models/list/ListModel";
import FormModel from "../models/form/FormModel";

import type DetailModel from "../models/detail/DetailModel";
import type TabModel from "../models/tab/TabModel";
import type ActionModel from "../models/actions/ActionModel";

/**
 * Retrieve all forms configured on a model with actions
 * @param model
 * @returns Object - { actionName: FormModel }
 */
export const useRetrieveFormsOnModel = (
  model: ListModel | DetailModel | TabModel,
): null | { [actionName: string]: FormModel } => {
  const modularui = useModularUIRequest();
  const [forms, setForms] = useState(null);
  const [requestCount, setRequestCount] = useState(0);
  const [responseCount, setResponseCount] = useState(0);

  useEffect(() => {
    const requests = getActions(model).map((action) => {
      const href = action.selfhref.addParameter("commit", "false");

      return modularui(href, { method: HTTP_METHODS.POST })
        .fetch()
        .then((form) => {
          if (form instanceof FormModel) {
            form.tokens = [];
          }
          return { [action.name]: form };
        });
    });

    setRequestCount(requests.length);

    Promise.all(requests).then((responses) => {
      // put response array in object
      const actionForms = Object.assign({}, ...responses);
      setForms(actionForms);
      setResponseCount(responses.length);
    });
  }, [model, modularui]);

  if (responseCount === requestCount) {
    return forms;
  }

  return null;
};

const getActions = (
  model: ListModel | DetailModel | TabModel,
): Array<ActionModel> => {
  if (!model.actionCollection) {
    return [];
  }

  const createActions = model.actionCollection
    .getActionsByType("create")
    .filter((action) => !action.isDisabled);

  const allActions = [...createActions];

  if (model instanceof ListModel) {
    model.listItemCollection.forEach((listItem) => {
      const updateActions = listItem.actionCollection
        .getActionsByType("update")
        .filter((action) => !action.isDisabled);

      allActions.push(...updateActions);
    });
  }

  const actions = [];
  const unique = [];
  allActions.forEach((action) => {
    if (!unique.includes(action.name)) {
      unique.push(action.name);
      actions.push(action);
    }
  });

  return actions;
};
